/**
 * Conteúdo do portfólio em formato de RPG: cada dado é uma CATEGORIA temática
 * e cada face sorteada revela um item específico dentro daquela categoria.
 *
 * Fonte de verdade factual: Curriculo_Walter_Andrade 2026.pdf
 * (resumos autorais — não inventar artigos, mentores ou métricas fora do CV).
 *
 * ------------------------------------------------------------------------
 * CONTRATO TÉCNICO (não quebrar — validado por scene/Dice + ui/InfoPanel):
 *  - `faces`: nº de faces FÍSICAS do dado. DEVE bater com a geometria real
 *    (ver scene/Dice.js e scene/Scene.js → `new Die(type, cfg.faces, 1)`).
 *    Por isso o nº de entradas por dado é fixo: 4, 6, 8, 10, 12, 20 e 10 (d%).
 *  - `entries`: objeto indexado pelo resultado do sorteio (1..faces).
 *  - d% é fisicamente um d10 marcado em dezenas (00,10,...,90). O valor 1..10
 *    sorteado indexa a entrada diretamente; o InfoPanel converte para o
 *    percentil apenas na exibição do número.
 * ------------------------------------------------------------------------
 *
 * Entradas com `placeholder: true` aparecem como "Em construção" até receberem
 * conteúdo real confirmado.
 */

export const DICE_ORDER = ['d4', 'd6', 'd8', 'd10', 'd12', 'd20', 'd100'];

/**
 * Ordem editorial para recrutamento: evidências de impacto e trajetória vêm
 * antes do inventário técnico. DICE_ORDER continua representando a ordem física.
 */
export const DISCOVERY_ORDER = [
  'd12',
  'd6',
  ...DICE_ORDER.filter((type) => !['d12', 'd6'].includes(type)),
];

/**
 * Metadados de cada categoria (dado).
 *  - `label`: rótulo do dado (D4, D%, …) exibido no chip/dock.
 *  - `name`:  nome completo da categoria (usado no dock/aria e no fallback).
 *  - `short`: versão curta para o chip do painel (evita quebra de linha feia).
 *  - `thesis`: enquadramento sênior — o que o visitante está sorteando aqui.
 */
export const DICE_CATEGORIES = {
  d4: {
    label: 'D4',
    name: 'Habilidades & Competências Técnicas',
    short: 'Craft técnico',
    thesis: 'O que eu construo com as mãos — stack, modelagem e operação.',
  },
  d6: {
    label: 'D6',
    name: 'Experiências Profissionais',
    short: 'Trajetória',
    thesis: 'Onde o craft foi testado em escala hospitalar e corporativa.',
  },
  d8: {
    label: 'D8',
    name: 'Formação, Certificações & Conhecimento',
    short: 'Fundação',
    thesis: 'O que sustenta julgamento técnico — formação, domínio e norte.',
  },
  d10: {
    label: 'D10',
    name: 'Referências, Escolas & Inspirações de Domínio',
    short: 'Critério',
    thesis: 'De onde vem o critério: escolas, domínio e cultura de decisão.',
  },
  d12: {
    label: 'D12',
    name: 'Projetos de Destaque & Casos Práticos',
    short: 'Casos com impacto',
    thesis: 'Decisões com resultado medido — custo, performance, operação.',
  },
  d20: {
    label: 'D20',
    name: 'Insights, Mentoria & Visão de Mercado',
    short: 'Princípios',
    thesis: 'Como penso trade-offs, liderança e mercado — sem slide de hype.',
  },
  d100: {
    label: 'D%',
    name: 'Parceiros, Clientes e Ecossistema',
    short: 'Ecossistema',
    thesis: 'Com quem e sobre o quê opero — plataformas, redes e domínio.',
  },
};

/**
 * Dados de conteúdo por categoria. Cada `entries[n]` = { title, text }.
 * (`placeholder: true` marca itens ainda dependentes de confirmação pessoal.)
 */
export const EXPERTISE_DATA = {
  // ---------------------------------------------------------------- D4
  d4: {
    faces: 4,
    entries: {
      1: {
        title: 'SQL multiplataforma & Modelagem analítica',
        text: 'SQL em Oracle, BigQuery e Databricks SQL — com plano de execução, custo e modelagem dimensional (star/snowflake) quando a pergunta de negócio exige. Menos join ornamental, mais consulta previsível em volume hospitalar e corporativo.',
      },
      2: {
        title: 'Python, Spark & Orquestração de Pipelines',
        text: 'Python (Pandas, PySpark, Prophet, google.cloud) para ingestão e transformação idempotentes. Orquestro em Airflow / Cloud Composer e Databricks Workflows com retries, alertas e SLA — pipeline que falha com rastro, não no escuro.',
      },
      3: {
        title: 'BI, DataViz & Modelo Semântico',
        text: 'Tableau, Power BI / Fabric, Looker Studio e MicroStrategy. Camada semântica confiável antes do gráfico bonito: o painel existe para encurtar o tempo até a decisão operacional e financeira.',
      },
      4: {
        title: 'GCP Data Platform & Lakehouse',
        text: 'BigQuery, Pub/Sub, Dataproc, Cloud Composer, Dataflow, GCS e Vertex. Particionamento, clustering e FinOps desde o desenho — custo como requisito, não como surpresa no fim do mês.',
      },
    },
  },

  // ---------------------------------------------------------------- D6
  d6: {
    faces: 6,
    entries: {
      1: {
        title: 'Tech Lead em Dados · Rede Americas (WAN)',
        text: 'Conduzo decisões de arquitetura, custo e entrega em engenharia de dados e BI na Rede Americas (nov/2022–atual). A migração Databricks → GCP reduziu 30% do custo operacional e elevou 70% o desempenho das consultas; dashboards associados aumentaram em 25% a eficiência das decisões operacionais. A execução combina GCP, Databricks e domínio profundo de MV e TASY.',
      },
      2: {
        title: 'Especialista em Dados / BI · Accenture (WAN)',
        text: 'Em atuação via WAN para a Accenture, reduzi dependência de ferramenta intermediária ao converter fluxos Alteryx em rotinas Databricks e SQL versionáveis e auditáveis (out/2025–jan/2026). Entreguei pipelines Python e dashboards para o ciclo da receita, conectando implementação técnica ao processo hospitalar em MV e TASY.',
      },
      3: {
        title: 'Especialista em Dados · Instituto Sócrates Guanaes',
        text: 'Estruturei a base analítica de nove unidades e liderei equipes multidisciplinares entre jan/2020 e jun/2023. Implantei DW Oracle, ETL Pentaho, Power BI e Gestão Visual com domínio dos processos de ERP hospitalar — financeiro, controladoria e logística.',
      },
      4: {
        title: 'Analista de Processos · Santa Casa da Bahia',
        text: 'Conectei processo, sistema e controle gerencial na implantação de soluções financeiras, logísticas e de repasse médico (ago/2019–jan/2020). Também desenvolvi indicadores financeiros e atuei na comissão especialista em ERP.',
      },
      5: {
        title: 'Consultor de Negócios · Hospital Santa Catarina/SP',
        text: 'Unifiquei dados estruturados e não estruturados de 13 hospitais para viabilizar analytics em rede (ago/2016–ago/2019). A solução combinou ETL Pentaho, dashboards estratégicos e conhecimento dos processos financeiros e do ERP hospitalar.',
      },
      6: {
        title: 'Analista de Sistemas · Hospital São Rafael/BA',
        text: 'Liderei a implantação da Gestão à Vista e transformei processos hospitalares em sistemas e indicadores entre mai/2012 e jul/2016. Oracle Forms & Reports foi a stack; o domínio financeiro, logístico e de suprimentos tornou-se a base operacional de como desenho dados hoje.',
      },
    },
  },

  // ---------------------------------------------------------------- D8
  d8: {
    faces: 8,
    entries: {
      1: {
        title: 'Pós-graduação · Gestão de Segurança da Informação',
        text: 'UNIJORGE (concl. Jul/2011). Base para pensar dado sensível, risco e controle — especialmente crítico em ambiente hospitalar, onde privacidade e disponibilidade não são opcionais.',
      },
      2: {
        title: 'Graduação · Administração com ênfase em SI',
        text: 'Faculdades São Salvador (concl. Ago/2009). Ponte entre negócio e sistema: modelar processo, negociar prioridade e traduzir demanda operacional em solução técnica sustentável.',
      },
      3: {
        title: 'Certificação · Cientista de Dados (DSA)',
        text: 'Data Science Academy. Fundamento analítico e estatístico aplicado ao dia a dia de engenharia e BI — do experimento à métrica que o negócio consegue usar.',
      },
      4: {
        title: 'Formação contínua · Lógica & Java (UNIFACS)',
        text: 'Base de programação e raciocínio estruturado que sustenta automações, ETL e revisão de código — o “como pensar” antes do “qual ferramenta”.',
      },
      5: {
        title: 'Inglês intermediário · Fisk',
        text: 'Leitura técnica, documentação cloud e colaboração em times globais (Accenture/WAN). Suficiente para consumir fonte primária; em evolução contínua.',
      },
      6: {
        title: 'Domínio documentado · ERP hospitalar (MV & TASY)',
        text: 'Anos de especialização nos processos sistêmicos hospitalares — financeiro, controladoria, logística, repasse. Traduzo o “chão do hospital” em modelo de dados e pipeline que o time de negócio reconhece.',
      },
      7: {
        title: 'Domínio documentado · Gestão à Vista & indicadores',
        text: 'Desde o São Rafael: cultura de indicador no ponto de decisão. Não é dashboard por vaidade — é ritual operacional com dono, meta e consequência.',
      },
      8: {
        title: 'Objetivo profissional (norte)',
        text: 'Atuar de forma estratégica em engenharia de dados e analytics — soluções inteligentes, escaláveis e orientadas a resultado de negócio. Dado como alavanca, não como relatório decorativo.',
      },
    },
  },

  // ---------------------------------------------------------------- D10
  d10: {
    faces: 10,
    entries: {
      1: {
        title: 'Escola dimensional · DW que responde pergunta real',
        text: 'Inspiração Kimball na prática: star/snowflake quando a pergunta é de negócio, não de ego técnico. Dimensão bem feita economiza reunião e refação.',
      },
      2: {
        title: 'Gestão à Vista · cultura de indicador no chão',
        text: 'Referência viva da trajetória no São Rafael: métrica visível, dono claro, ciclo curto de correção. BI sem ritual operacional vira tela bonita e decisão igual.',
      },
      3: {
        title: 'FinOps · custo como requisito de arquitetura',
        text: 'A migração Databricks → GCP (−30% custo, +70% performance) cristalizou a lição: particionamento, formato e orquestração decidem o boleto antes do tuning heróico.',
      },
      4: {
        title: 'DataOps · orquestração com rastro',
        text: 'Composer/Airflow, Workflows Databricks, YAML e Parquet com observabilidade. Pipeline sem alerta é incidente adiado — aprendi isso operando, não em slide.',
      },
      5: {
        title: 'Ecossistema MV & TASY · processo antes do pixel',
        text: 'Inspiração de domínio: entender o fluxo hospitalar (financeiro, logística, repasse) antes de modelar. Sem processo, o datamart mente com precisão.',
      },
      6: {
        title: 'Analytics Engineering · ponte eng ↔ negócio',
        text: 'O ofício entre pipeline e painel: contrato de dados, camada semântica e narrativa executiva. Menos “entreguei a tabela”, mais “habilitei a decisão”.',
      },
      7: {
        title: 'SCRUM & LEAN · entrega contínua em dados',
        text: 'Gestão ágil aplicada a backlog analítico: fatias de valor, WIP controlado, retrospectiva que muda o processo — não só o board.',
      },
      8: {
        title: 'Lakehouse mindset · Databricks × GCP',
        text: 'Convivi com os dois mundos: notebooks/clusters e BigQuery/Pub/Sub/Dataproc. A inspiração não é a marca — é escolher a ferramenta pelo workload e pelo custo.',
      },
      9: {
        title: 'Rede hospitalar · escala multiunidade',
        text: 'Do ETL de 9 unidades (ISG) à unificação de 13 hospitais (Santa Catarina): a inspiração é governança e padronização sem matar a operação local.',
      },
      10: {
        title: 'Decisão por evidência · combate ao HiPPO na saúde',
        text: 'Em ambiente hospitalar e corporativo, o dado bom perde para a opinião hierárquica se a cultura não muda. Inspiro times a perguntar “o que o indicador mostra?” antes de “quem mandou”.',
      },
    },
  },

  // ---------------------------------------------------------------- D12
  d12: {
    faces: 12,
    entries: {
      1: {
        title: 'Migração estratégica Databricks → GCP',
        text: 'Reduzi 30% dos custos operacionais e aumentei 70% o desempenho das consultas ao conduzir a migração Databricks → GCP na Rede Americas. O caso demonstra FinOps e performance tratados como uma única decisão de arquitetura.',
      },
      2: {
        title: 'Pipelines avançados em GCP',
        text: 'Estruturei automações de ponta a ponta com contrato de schema e falha observável. Pub/Sub, Dataproc, Parquet, YAML e Vertex entram como meios para tornar ingestão e processamento previsíveis — não como inventário de ferramentas.',
      },
      3: {
        title: 'Dashboards que moveram a operação (+25%)',
        text: 'Elevei em 25% a eficiência das decisões operacionais com painéis estratégicos na Rede Americas. O resultado veio de BI ligado a dono, rotina e ação — não de uma camada visual isolada.',
      },
      4: {
        title: 'De Alteryx para SQL nativo',
        text: 'Aumentei controle e auditabilidade ao reescrever fluxos Alteryx como rotinas Databricks e SQL na Accenture. A mudança reduziu caixa-preta visual e devolveu versionamento, teste e ownership ao time de dados.',
      },
      5: {
        title: 'Analytics do ciclo da receita',
        text: 'Transformei atraso de dado em visibilidade acionável sobre o ciclo da receita hospitalar. Dashboards e pipelines aproximaram operação e financeiro de um processo em que atraso de informação vira atraso de caixa.',
      },
      6: {
        title: 'ETL multiunidade · 9 hospitais (ISG)',
        text: 'Padronizei carga, qualidade e janela operacional para nove unidades sem perder rastreabilidade. Oracle e Pentaho sustentaram uma operação de ETL desenhada para escala geográfica.',
      },
      7: {
        title: 'Data Warehouse em Oracle (ISG)',
        text: 'Criei uma base analítica comum para a rede ao desenhar e implantar o Data Warehouse Oracle do ISG. A entrega cobriu modelagem, carga e consumo por Power BI e Gestão Visual.',
      },
      8: {
        title: 'Unificação de 13 hospitais (Santa Catarina)',
        text: 'Unifiquei dados estruturados e não estruturados de 13 hospitais para formar um mapa analítico da rede. O ETL Pentaho criou a fundação necessária para BI de grupo com contexto comum.',
      },
      9: {
        title: 'Gestão à Vista no São Rafael',
        text: 'Levei indicadores ao ponto de decisão ao liderar a implantação da Gestão à Vista no São Rafael. A entrega combinou ritual de acompanhamento, responsabilidade clara e cultura de performance operacional.',
      },
      10: {
        title: 'Sistemas financeiros & repasse médico',
        text: 'Aproximei sistemas, caixa e controle na implementação de soluções financeiras, logísticas e de repasse médico na Santa Casa da Bahia. Indicadores estratégicos deram suporte à operação e à conformidade.',
      },
      11: {
        title: 'Especialização MV & TASY end-to-end',
        text: 'Reduzo discovery e retrabalho ao traduzir processos hospitalares em requisitos de engenharia e BI. O domínio de MV e TASY cobre financeiro, controladoria, logística e suas dependências de ponta a ponta.',
      },
      12: {
        title: 'Liderança técnica multidisciplinar',
        text: 'Escalo resultado além da execução individual por mentoria, revisão de arquitetura e transferência de contexto. Do ISG à Rede Americas, liderei times mistos de engenharia, BI e negócio com responsabilidade compartilhada pela entrega.',
      },
    },
  },

  // ---------------------------------------------------------------- D20
  d20: {
    faces: 20,
    entries: {
      1: { title: 'Dado é meio, decisão é fim', text: 'Pipeline e dashboard só valem pelo que mudam na decisão. Métrica que ninguém usa para decidir é custo, não ativo.' },
      2: { title: 'Diagnóstico antes da solução', text: 'Resolver rápido o problema errado custa mais caro do que demorar no problema certo. Causa raiz primeiro, arquitetura depois — lição reforçada em migrações e trocas de ferramenta.' },
      3: { title: 'Custo é requisito, não detalhe', text: 'FinOps começa na modelagem. A migração GCP (−30% custo) mostrou: a tabela certa economiza mais do que qualquer tuning posterior de query.' },
      4: { title: 'Hype × maturidade', text: 'A ferramenta nova nem sempre resolve o problema real. Já vivi Databricks, GCP, Alteryx, Pentaho, ODI — escolho pelo workload e pelo TCO, não pelo slide de marketing.' },
      5: { title: 'Build × buy', text: 'Custo total de propriedade, lock-in e diferencial estratégico vêm antes da decisão de construir ou comprar. Substituir Alteryx por SQL foi buy→build consciente.' },
      6: { title: 'Governança como vantagem', text: 'Governança não é burocracia: é o que permite escalar dados de 9 ou 13 unidades com confiança e sem explosão de custo.' },
      7: { title: 'Observabilidade não é opcional', text: 'Pipeline sem observabilidade é dívida técnica silenciosa que só aparece como incidente na frente do stakeholder.' },
      8: { title: 'Mentoria multiplica', text: 'Elevo o time por revisão de código, padrões e transferência de contexto — impacto que escala além do meu teclado.' },
      9: { title: 'Lakehouse × warehouse centralizado', text: 'O modelo se decide pela maturidade real do time e custo de coordenação, não pela tendência do momento. Já operei os dois lados.' },
      10: { title: 'Cultura data-driven combate o HiPPO', text: 'Decisão por evidência, não pela opinião do mais bem pago. Em saúde e operação, dado bom perde para política ruim se a cultura não muda.' },
      11: { title: 'Qualidade é pré-requisito', text: 'Dashboard bonito sobre dado sujo é decisão errada com casas decimais. Confiabilidade vem antes da estética — sobretudo com ERP hospitalar na origem.' },
      12: { title: 'Trade-off explícito', text: 'Torno visível o custo de gambiarras e o de over-engineering, e decido com essa tensão à mesa — não no escuro.' },
      13: { title: 'Dados como produto', text: 'Usuário, problema real, valor entregue e ciclo de feedback. Entregar tabela não é entregar produto de dados.' },
      14: { title: 'Priorização por valor e risco', text: 'Foco no que move o ponteiro operacional/financeiro ou reduz risco maior; digo "não" com justificativa ao resto.' },
      15: { title: 'Reprodutibilidade sobre memória', text: 'Versiono (GitHub, Azure DevOps, Bitbucket), documento e automatizo. Confio no processo auditável, não na lembrança de quem construiu.' },
      16: { title: 'Comunicação executiva', text: 'Traduzo custo de infra e débito de arquitetura em decisão clara para quem não é técnico — linguagem de margem, prazo e risco.' },
      17: { title: 'Liderança sem cargo', text: 'Influencio arquitetura e qualidade pela consistência técnica. O título de Tech Lead só formaliza o que a prática já sustentava.' },
      18: { title: 'Risco antes da migração', text: 'Mapeio pontos de falha e plano de rollback antes de qualquer migração crítica em produção — a lição da troca Databricks→GCP em escala.' },
      19: { title: 'Benchmark honesto', text: 'Comparo soluções no meu volume real de dados hospitalares/corporativos, não com o benchmark de marketing do fornecedor.' },
      20: {
        title: 'NAT 20 — Visão sistêmica end-to-end',
        text: 'Acerto crítico: conecto ingestão (ERP/HISS), modelagem, BI e decisão de negócio num sistema coeso — vejo a floresta hospitalar e a árvore do pipeline ao mesmo tempo.',
      },
    },
  },

  // ---------------------------------------------------------------- D%
  d100: {
    faces: 10,
    entries: {
      1: {
        title: 'Ecossistema · Google Cloud (BigQuery, Composer, GCS…)',
        text: 'Plataforma central: BigQuery, Pub/Sub, Dataproc, Cloud Composer, Dataflow, Cloud Storage e Vertex. Analytics, orquestração e lake com FinOps na mesma conversa.',
      },
      2: {
        title: 'Ecossistema · Databricks Lakehouse',
        text: 'Notebooks, Workflows, SQL Warehouse e Clusters. Base da operação antes/durante a transição para GCP — e ainda presente em projetos WAN/Accenture.',
      },
      3: {
        title: 'Ecossistema · BI & Visualização',
        text: 'Power BI / Fabric, Tableau, Looker Studio e MicroStrategy — camada de consumo e decisão sobre o modelo semântico.',
      },
      4: {
        title: 'Cliente / Rede · Americas (WAN Tech Lead)',
        text: 'Contexto atual de liderança técnica em dados e BI. Migração cloud, pipelines GCP e dashboards operacionais com impacto medido em custo e performance.',
      },
      5: {
        title: 'Parceiro · Accenture (via WAN)',
        text: 'Engajamento em pipelines Python/Databricks, modernização Alteryx→SQL e analytics de ciclo da receita em ambiente hospitalar.',
      },
      6: {
        title: 'Cliente · Instituto Sócrates Guanaes',
        text: 'Rede multiunidade: DW Oracle, ETL Pentaho para 9 unidades, Power BI e gestão de times multidisciplinares.',
      },
      7: {
        title: 'Rede hospitalar · Santa Catarina, São Rafael & Santa Casa',
        text: 'Trajetória em hospitais e redes: unificação de 13 unidades, Gestão à Vista, sistemas financeiros/repasse e comissão de ERP — ecossistema de saúde como escola de domínio.',
      },
      8: {
        title: 'Stack clássica · Oracle, ODI, Pentaho & ADF',
        text: 'ETL/DW tradicional ainda presente em muitas operações: Oracle, ODI, Pentaho, Azure Data Factory e Dataflow. Modernizo sem desprezar o legado que paga o boleto.',
      },
      9: {
        title: 'Domínio · ERP / HISS hospitalar (MV & TASY)',
        text: 'Especialização sistêmica nos principais ERPs hospitalares do Brasil. Parceria implícita com operação clínica-administrativa: sem esse mapa, o datamart erra o diagnóstico.',
      },
      10: {
        title: 'Credenciais & versionamento',
        text: 'Formação em SI + Segurança da Informação; certificação Cientista de Dados (DSA). Versionamento em GitHub, Azure DevOps e Bitbucket — colaboração com rastro.',
      },
    },
  },
};

/** Retorna a entrada de conteúdo para um dado e um valor sorteado (1..faces). */
export function getEntry(dieType, value) {
  const die = EXPERTISE_DATA[dieType];
  if (!die) return null;
  return die.entries[value] ?? null;
}

/** Retorna os metadados de categoria de um dado. */
export function getCategory(dieType) {
  return DICE_CATEGORIES[dieType] ?? null;
}
